import { useState } from "react";
import { useParams } from "react-router";
import { MailIcon } from "vega-icons";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { Checkbox } from "@vega-core/components/shadcn/checkbox";
import { m } from "@vega-core/paraglide/messages";

import { CoInvestmentPageHeader } from "../components/CoInvestmentPageHeader/CoInvestmentPageHeader";
import { ScheduleSendPopover } from "../components/SendDrafts/ScheduleSendPopover";
import { SendDraftRow } from "../components/SendDrafts/SendDraftRow";
import { useSendDraftsManagement } from "../components/SendDrafts/useSendDraftsManagement";
import { useCoInvestDealReadOnly } from "../hooks/useCoInvestDealReadOnly";
import { useDealName } from "../hooks/useDealName";

export const SendDraftsPage = () => {
  const { id: dealId = "" } = useParams<{ id: string }>();
  const { dealName } = useDealName(dealId);
  const { isMutationLocked } = useCoInvestDealReadOnly();

  const {
    rows,
    isLoading,
    selectedIds,
    sendableIds,
    selectAllState,
    toggleSelected,
    toggleSelectAll,
    sendSelected,
    isSending,
    scheduleSelected,
    isScheduling,
    isAutoSaving,
    onDraftSaved,
    navigateToShare,
  } = useSendDraftsManagement(dealId);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = (draftId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);

      if (next.has(draftId)) {
        next.delete(draftId);
      } else {
        next.add(draftId);
      }

      return next;
    });
  };

  const allExpanded = rows.length > 0 && expandedIds.size === rows.length;

  const toggleAllExpanded = () => {
    setExpandedIds(
      allExpanded ? new Set() : new Set(rows.map((row) => row.draft.id)),
    );
  };

  const sendDisabled =
    isMutationLocked ||
    isSending ||
    isScheduling ||
    isAutoSaving ||
    sendableIds.length === 0;

  if (isLoading) {
    return (
      <div className="flex min-h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="mx-auto my-4 flex max-w-4xl flex-col gap-6 pb-24">
      <CoInvestmentPageHeader
        backButtonDataTestId="send-drafts-back-button"
        description={m.coInvestments_sendDrafts_description({
          count: rows.length,
          dealName,
        })}
        fundName={dealName}
        onBack={navigateToShare}
        showClosedDealNotice={isMutationLocked}
        title={m.coInvestments_sendDrafts_title()}
      />

      {rows.length === 0 ? (
        <p
          className="body-base-regular py-10 text-center text-text/secondary"
          data-testid="send-drafts-empty"
        >
          {m.coInvestments_sendDrafts_empty()}
        </p>
      ) : (
        <>
          <div className="flex items-center justify-between border-y border-border/primary py-3">
            <label className="flex cursor-pointer items-center gap-2 body-base-regular text-text/primary">
              <Checkbox
                aria-label={m.coInvestments_sendDrafts_selectAll()}
                checked={selectAllState}
                className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-default focus-visible:ring-offset-1"
                disabled={isMutationLocked}
                onCheckedChange={(value) => toggleSelectAll(value === true)}
              />
              <span className="tabular-nums">
                {m.coInvestments_sendDrafts_selectedCount({
                  selected: sendableIds.length,
                  total: rows.length,
                })}
              </span>
            </label>
            <ThemedButton
              dataTestId="send-drafts-toggle-all"
              onClick={toggleAllExpanded}
              variant="text"
            >
              {m.coInvestments_sendDrafts_toggleAll()}
            </ThemedButton>
          </div>

          <div className="flex flex-col gap-3">
            {rows.map((row) => (
              <SendDraftRow
                checked={selectedIds.has(row.draft.id) && row.hasRecipients}
                dealId={dealId}
                disabled={!row.hasRecipients || isMutationLocked}
                expanded={expandedIds.has(row.draft.id)}
                key={row.draft.id}
                onSaved={onDraftSaved}
                onToggleChecked={(checked) =>
                  toggleSelected(row.draft.id, checked)
                }
                onToggleExpanded={() => toggleExpanded(row.draft.id)}
                readOnly={isMutationLocked}
                row={row}
              />
            ))}
          </div>

          <div className="sticky bottom-0 flex items-center justify-between border-t border-border/primary bg-background/primary py-3">
            <ThemedButton
              dataTestId="send-drafts-cancel"
              disabled={isSending || isScheduling}
              onClick={navigateToShare}
              variant="secondary"
            >
              {m.btn_cancel()}
            </ThemedButton>
            <div className="flex items-center gap-2">
              <ScheduleSendPopover
                disabled={sendDisabled}
                isScheduling={isScheduling}
                onConfirm={scheduleSelected}
              />
              <ThemedButton
                dataTestId="send-drafts-send"
                disabled={sendDisabled}
                icon={<MailIcon className="size-4" />}
                iconPosition="left"
                isLoading={isSending}
                onClick={sendSelected}
              >
                {sendableIds.length > 0
                  ? m.coInvestments_sendDrafts_sendCount({
                      count: sendableIds.length,
                    })
                  : m.coInvestments_sendDrafts_send()}
              </ThemedButton>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
